import React, { useState } from 'react';
import { Plus, Search, Filter } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { DataTable } from '../../components/ui/DataTable';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Pagination } from '../../components/ui/Pagination';
import { mockBatches } from '../../data/mockData';
import { useAuth } from '../../contexts/AuthContext';
interface PaymentBatchListProps {
  onNewBatch: () => void;
  onViewBatch: (id: string) => void;
}
export function PaymentBatchList({
  onNewBatch,
  onViewBatch
}: PaymentBatchListProps) {
  const {
    isMaker,
    isBranch
  } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  // Filter logic
  const filteredBatches = mockBatches.filter(b => {
    const matchesSearch = b.fileName.toLowerCase().includes(searchTerm.toLowerCase()) || b.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || statusFilter === 'pending' && (b.status === 'Pending' || b.status === 'ApprovalPending' || b.status === 'TitleFetchInitiated') || statusFilter === 'completed' && (b.status === 'Completed' || b.status === 'PaymentInitiated') || statusFilter === 'rejected' && (b.status === 'Rejected' || b.status === 'Failed');
    return matchesSearch && matchesStatus;
  });
  // Pagination logic
  const totalPages = Math.ceil(filteredBatches.length / itemsPerPage);
  const paginatedBatches = filteredBatches.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  return <div className="space-y-6" data-id="element-366">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-367">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-368">Payment Batches</h2>
        {isBranch && isMaker && <Button onClick={onNewBatch} leftIcon={<Plus className="w-4 h-4" data-id="element-370" />} data-id="element-369">
            New Bulk Payment
          </Button>}
      </div>

      <div className="flex flex-col sm:flex-row gap-4 bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-id="element-371">
        <div className="flex-1" data-id="element-372">
          <Input placeholder="Search by file name or batch ID..." leftIcon={<Search className="w-4 h-4" data-id="element-374" />} value={searchTerm} onChange={e => {
          setSearchTerm(e.target.value);
          setCurrentPage(1); // Reset to page 1 on search
        }} data-id="element-373" />
        </div>
        <div className="w-full sm:w-48" data-id="element-375">
          <Select options={[{
          value: 'all',
          label: 'All Statuses'
        }, {
          value: 'pending',
          label: 'Pending'
        }, {
          value: 'completed',
          label: 'Completed'
        }, {
          value: 'rejected',
          label: 'Rejected'
        }]} value={statusFilter} onChange={e => {
          setStatusFilter(e.target.value);
          setCurrentPage(1);
        }} data-id="element-376" />
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden" data-id="element-377">
        <DataTable columns={[{
        header: 'File Name',
        accessor: 'fileName',
        className: 'font-medium'
      }, {
        header: 'Processing Date',
        accessor: b => new Date(b.uploadDate).toLocaleDateString()
      }, {
        header: 'Status',
        accessor: b => <StatusBadge status={b.status} size="sm" data-id="element-379" />
      }, {
        header: 'Records',
        accessor: 'totalRecords'
      }, {
        header: 'Valid',
        accessor: b => <span className="text-green-600 font-medium" data-id="element-380">
                  {b.validRecords}
                </span>
      }, {
        header: 'Successful',
        accessor: b => <span className="text-blue-600 font-medium" data-id="element-381">
                  {b.successfulRecords || 0}
                </span>
      }, {
        header: 'Failed',
        accessor: b => <span className={b.failedRecords > 0 ? 'text-red-600 font-medium' : 'text-gray-500'} data-id="element-382">
                  {b.failedRecords}
                </span>
      }, {
        header: 'Batch Total',
        accessor: b => `PKR ${b.totalAmount.toLocaleString()}`,
        className: 'font-mono text-right'
      }, {
        header: 'Actions',
        accessor: b => <Button variant="ghost" size="sm" className="text-[#1B6B3A]" onClick={() => onViewBatch(b.id)} data-id="element-383">
                  View
                </Button>
      }]} data={paginatedBatches} data-id="element-378" />

        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} data-id="element-384" />
      </div>
    </div>;
}